"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  ASCII_FONT_IDS,
  type AsciiFontId,
  asciiArtCodeBlock,
  DEFAULT_ASCII_OPTIONS,
  FILLED_FONTS,
  MAX_INPUT_CHARACTERS,
  MAX_SPACING,
  renderAsciiArt,
  TERMINAL_WIDTH,
} from "@/lib/tool-kit/text/ascii-art";
import { displayCharacter } from "@/lib/tool-kit/text/code-points";

/** English strings. A translated content file overrides these through `ui`. */
export const ASCII_ART_GENERATOR_STRINGS = {
  inputLabel: "Your text",
  inputPlaceholder: "Type a word or a short phrase",
  defaultText: "Hello",
  inputHint:
    "Letters, digits and common punctuation. Each line you type becomes its own banner.",
  fontLabel: "Font",
  fonts: {
    block: "Block",
    shadow: "Shadow",
    slim: "Slim",
    small: "Small",
  } as Record<AsciiFontId, string>,
  spacingLabel: "Space between letters",
  spacingOption: "one: {count} column|other: {count} columns",
  fillLabel: "Fill character",
  fillHint:
    "One single-width character such as # @ * or █. Emoji are double width and push the letters out of line.",
  shadowLabel: "Shadow character",
  selfFill: "Draw each letter with itself (an H made of H's)",
  results: "Your ASCII art",
  artLabel: "ASCII art of the text: {text}",
  widthStat: "one: {count} column wide|other: {count} columns wide",
  rowsStat: "one: {count} line|other: {count} lines",
  copy: "Copy",
  copied: "Copied!",
  copyCode: "Copy as Markdown code block",
  download: "Download .txt",
  filename: "ascii-art.txt",
  empty: "Type some text above to draw it.",
  missing: "Not in this font, so left out: {list}",
  tooWide:
    "Wider than 80 columns, so it may wrap in a terminal, an email or a narrow window. Put part of the text on a second line.",
  truncated: "Only the first {count} characters are drawn.",
  monospaceNote:
    "ASCII art only lines up in a monospaced font. Paste it into a code block, a terminal, a source-code comment or a plain-text file.",
} as const;

type Strings = typeof ASCII_ART_GENERATOR_STRINGS;

const SPACINGS = Array.from({ length: MAX_SPACING + 1 }, (_, index) =>
  String(index)
);

function ArtStats({
  width,
  rows,
  strings,
}: {
  width: number;
  rows: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <p className={HINT}>
      {tPlural(strings.widthStat, locale, width, { count: fmt(width) })}
      {" · "}
      {tPlural(strings.rowsStat, locale, rows, { count: fmt(rows) })}
    </p>
  );
}

export default function AsciiArtGeneratorWidget() {
  const strings = useToolUi(ASCII_ART_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<string>(strings.defaultText);
  const [font, setFont] = useState<AsciiFontId>(DEFAULT_ASCII_OPTIONS.font);
  const [spacing, setSpacing] = useState(String(DEFAULT_ASCII_OPTIONS.spacing));
  const [fill, setFill] = useState(DEFAULT_ASCII_OPTIONS.fill);
  const [shadow, setShadow] = useState(DEFAULT_ASCII_OPTIONS.shadow);
  const [selfFill, setSelfFill] = useState(false);
  const deferredInput = useDeferredValue(input);
  const art = useMemo(
    () =>
      renderAsciiArt(deferredInput, {
        font,
        spacing: Number(spacing),
        fill,
        selfFill,
        shadow,
      }),
    [deferredInput, font, spacing, fill, selfFill, shadow]
  );
  const filled = FILLED_FONTS.has(font);

  return (
    <>
      <TextAreaField
        hint={strings.inputHint}
        id="ascii-art-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        prose
        rows={2}
        value={input}
      />
      <PillGroup
        label={strings.fontLabel}
        name="ascii-art-font"
        onChange={setFont}
        options={ASCII_FONT_IDS.map((value) => ({
          value,
          label: strings.fonts[value],
        }))}
        value={font}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          id="ascii-art-spacing"
          label={strings.spacingLabel}
          onChange={setSpacing}
          options={SPACINGS.map((value) => ({
            value,
            label: tPlural(strings.spacingOption, locale, Number(value), {
              count: fmt(Number(value)),
            }),
          }))}
          value={spacing}
        />
        {filled ? (
          <TextField
            hint={strings.fillHint}
            id="ascii-art-fill"
            label={strings.fillLabel}
            mono
            onChange={setFill}
            value={fill}
          />
        ) : null}
        {font === "shadow" ? (
          <TextField
            id="ascii-art-shadow"
            label={strings.shadowLabel}
            mono
            onChange={setShadow}
            value={shadow}
          />
        ) : null}
        {filled ? (
          <div className="flex items-center">
            <Checkbox
              checked={selfFill}
              label={strings.selfFill}
              onChange={setSelfFill}
            />
          </div>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {art.text.length === 0 ? (
          <Notice>{strings.empty}</Notice>
        ) : (
          <div className={`${RESULT_CARD} space-y-3`}>
            <pre
              aria-label={t(strings.artLabel, { text: deferredInput })}
              className="overflow-x-auto whitespace-pre font-mono text-foreground text-xs leading-tight [overflow-wrap:normal] sm:text-sm"
              role="img"
            >
              {art.text}
            </pre>
            <ArtStats rows={art.rows} strings={strings} width={art.width} />
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copy}
            text={art.text}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyCode}
            text={asciiArtCodeBlock(art.text)}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={art.text.length === 0}
            onClick={() => downloadText(strings.filename, `${art.text}\n`)}
            type="button"
          >
            {strings.download}
          </button>
        </div>
        {art.missing.length > 0 ? (
          <p className={HINT}>
            {t(strings.missing, {
              list: art.missing.map(displayCharacter).join(" "),
            })}
          </p>
        ) : null}
        {art.width > TERMINAL_WIDTH ? (
          <p className={HINT}>{strings.tooWide}</p>
        ) : null}
        {art.truncated ? (
          <p className={HINT}>
            {t(strings.truncated, { count: fmt(MAX_INPUT_CHARACTERS) })}
          </p>
        ) : null}
        <p className={HINT}>{strings.monospaceNote}</p>
      </section>
    </>
  );
}
